// deno-lint-ignore-file no-unused-vars
// Tworzenie zadań: strefy wstawiania między wierszami (i na końcu każdego poziomu) z dwoma
// przyciskami — „＋ Zadanie” (ten poziom, w tym miejscu) i „＋ Podzadanie” (dla wiersza powyżej) —
// oraz rozwijany formularz z pełnym zestawem pól. Spec: spec/lista-zadan.md → "Tworzenie zadań".
import { h, Fragment } from '../jsx-runtime.ts';
import type { TaskWithTags, FilterState, Tag, CreateTaskInput } from '../types.ts';
import { TasksAPI } from '../api.ts';
import { showToast } from './tmk-toast.tsx';
import { UndoManager } from '../undo.ts';
import { renderTagBadge } from './tmk-tag-badge.tsx';
import { parseDuration, DURATION_HINT } from '../duration.ts';
import { renderRecurrencePicker } from './tmk-recurrence-picker.tsx';

export interface NewTaskRowCallbacks {
  onRerender: () => Promise<void>;
  getInheritedTagIds: (parentId: string | null) => { tag_id: string }[];
  getAllTags: () => Tag[];
  /** Zadanie utworzone — lista otworzy po rerenderze następny formularz tuż pod nim (spec: Kolejne zadanie po Enter). */
  onCreated: (parentId: string | null, createdPosition: number) => void;
}

/** Jeden cel wstawienia w strefie (spec: Tworzenie zadań → „Cele strefy”). */
export interface InsertTarget {
  parentId: string | null;
  /** Pozycja wśród rodzeństwa; undefined = na koniec (backend dopisuje). */
  position: number | undefined;
  /** 0 = ten sam poziom („Zadanie”), k ≥ 1 = podzadanie k poziomów głębiej. */
  level: number;
  /** Tytuł zadania-rodzica (etykieta, tooltip, nagłówek formularza); null tylko na poziomie korzenia listy. */
  parentTitle: string | null;
  /** Liczba istniejących zadań na poziomie celu — do dopasowania „następnego formularza” (new-task-core.ts). */
  siblingCount: number;
}

export interface InsertZoneOptions {
  targets: InsertTarget[];
  /** Głębokość poziomu — na dotyku ustawia wcięcie szyny z przyciskami (zmienna CSS --zone-depth). */
  depth: number;
  /** Indeks celu, którego formularz ma się otworzyć od razu po wstawieniu strefy do DOM (następne zadanie po Enter). */
  autoOpen?: number;
}

/**
 * Ikony przycisków strefy — mini-diagramy (spec: Tworzenie zadań): pusty węzeł = wiersz powyżej,
 * węzeł z plusem = nowe zadanie. Pionowa kreska = ten sam poziom, kolanko w prawo = zagłębienie.
 * SVG budowane ze stałego markupu (bez treści użytkownika), więc innerHTML jest bezpieczne.
 */
const ICON_SAME = `<svg viewBox="0 0 24 24" aria-hidden="true">
  <circle class="node-prev" cx="12" cy="5" r="3.4"/>
  <path class="link" d="M12 8.4v4.2"/>
  <circle class="node-new" cx="12" cy="17.4" r="5.2"/>
  <path class="plus" d="M12 14.9v5M9.5 17.4h5"/>
</svg>`;
const ICON_CHILD = `<svg viewBox="0 0 24 24" aria-hidden="true">
  <circle class="node-prev" cx="6.5" cy="5" r="3.4"/>
  <path class="link" d="M6.5 8.4v9h5.5"/>
  <circle class="node-new" cx="17.3" cy="17.4" r="5.2"/>
  <path class="plus" d="M17.3 14.9v5M14.8 17.4h5"/>
</svg>`;

function pillIcon(kind: 'same' | 'child'): HTMLElement {
  const span = <span className="pill-icon" /> as HTMLElement;
  span.innerHTML = kind === 'same' ? ICON_SAME : ICON_CHILD;
  return span;
}

function hasDateFilter(filterState: FilterState | undefined): boolean {
  return !!(filterState?.dateFrom || filterState?.dateTo);
}

const DATE_FILTER_MSG = 'Wyłącz filtr dat, aby dodawać zadania';

function shortTitle(title: string, max = 22): string {
  const t = title.trim();
  return t.length > max ? t.slice(0, max - 1).trimEnd() + '…' : t;
}

/**
 * Strefa wstawiania z pastylką na każdy osiągalny cel: „Zadanie” (ten poziom) oraz „Podzadanie · X”
 * dla łańcucha ostatnich rozwiniętych potomków wiersza powyżej. Na desktopie środki pastylek leżą
 * w (2i+1)/2n szerokości linii (dla dwóch: 1/4 i 3/4). Element bez `data-task-id`, więc D&D
 * i skróty klawiszowe go pomijają.
 */
export function renderInsertZone(
  filterState: FilterState | undefined,
  callbacks: NewTaskRowCallbacks,
  opts: InsertZoneOptions,
): HTMLElement {
  const zone = <div className="task-node task-insert-zone" /> as HTMLElement;
  zone.style.setProperty('--zone-depth', String(opts.depth));
  const blocked = hasDateFilter(filterState);
  const line = <div className="task-insert-line" /> as HTMLElement;
  zone.appendChild(line);

  if (blocked) {
    zone.classList.add('disabled');
    zone.title = DATE_FILTER_MSG;
    const msg = <span className="task-insert-pill same">{DATE_FILTER_MSG}</span> as HTMLElement;
    msg.style.left = '50%';
    line.appendChild(msg);
    return zone;
  }

  const openForm = (target: InsertTarget) => {
    const heading = target.parentTitle ? 'Podzadanie dla: ' + target.parentTitle : '';
    const form = renderExpandedNewTask(target.parentId, target.position, callbacks, heading, () => form.replaceWith(zone), '');
    // Formularz w linii z kartami docelowego poziomu: karty mają margin-left = głębokość × wcięcie; cel
    // głębszy o k poziomów dokłada k offsetów listy podzadań (formularz renderuje się w kontenerze strefy).
    const depth = opts.depth + target.level;
    form.style.marginLeft = `calc(${depth} * var(--indent-size, 12px) + ${target.level} * var(--subtree-offset, 42px))`;
    zone.replaceWith(form);
  };

  const n = opts.targets.length;
  opts.targets.forEach((target, i) => {
    const isSame = target.level === 0;           // ikona pionowa = ten sam poziom co wiersze wokół
    const isRoot = isSame && !target.parentTitle;
    // Nazewnictwo (spec): „Zadanie” tylko na poziomie 0; pod rodzicem zawsze „Podzadanie · rodzic”.
    const label = isRoot ? 'Zadanie' : 'Podzadanie · ' + shortTitle(target.parentTitle ?? '');
    const tip = isRoot
      ? 'Dodaj zadanie na najwyższym poziomie, w tym miejscu'
      : isSame
        ? 'Dodaj podzadanie do: ' + (target.parentTitle ?? '') + ' (w tym miejscu)'
        : 'Dodaj podzadanie do: ' + (target.parentTitle ?? '') + ' (na końcu)';
    const pill = <button type="button" className={'task-insert-pill ' + (isSame ? 'same' : 'child')}
      aria-label={tip} title={tip} /> as HTMLElement;
    pill.style.left = `calc(${2 * i + 1} / ${2 * n} * 100%)`;   // desktop: równomiernie na linii
    pill.style.setProperty('--pill-level', String(target.level));  // dotyk: wcięcie wg głębokości celu
    pill.appendChild(pillIcon(isSame ? 'same' : 'child'));
    pill.appendChild(<span className="pill-label">{label}</span> as HTMLElement);
    pill.addEventListener('click', (e: Event) => { e.stopPropagation(); openForm(target); });
    line.appendChild(pill);
  });

  // Klik w samą linię (poza pastylkami) = pierwszy cel (zadanie na tym poziomie).
  zone.addEventListener('click', (e: Event) => { e.stopPropagation(); if (opts.targets[0]) openForm(opts.targets[0]); });
  zone.title = 'Dodaj zadanie w tym miejscu';

  // Następne zadanie po Enter/Dodaj: strefa pod świeżo utworzonym zadaniem otwiera się sama (po wstawieniu do DOM).
  const auto = opts.autoOpen !== undefined ? opts.targets[opts.autoOpen] : undefined;
  if (auto) queueMicrotask(() => { if (zone.isConnected) openForm(auto); });
  return zone;
}

/**
 * Stałe pole „Nowe zadanie…” na samym końcu listy (spec: Tworzenie zadań → Stałe pole na końcu): jedyne
 * miejsce do wpisania bez klikania. Fokus rozwija je w pełny formularz w tym samym miejscu; zwinięcie
 * (Escape / klik poza z pustym tytułem) przywraca kompaktowy input.
 */
export function renderPersistentNewTask(
  filterState: FilterState | undefined,
  callbacks: NewTaskRowCallbacks,
  target: InsertTarget,
  depth: number,
  autoFocus: boolean,
): HTMLElement {
  const node = <div className="task-node task-node-new task-new-persistent" /> as HTMLElement;
  node.style.marginLeft = `calc(${depth} * var(--indent-size, 12px))`;
  const input = <input type="text" className="inline-new-input task-new-persistent-input"
    placeholder={target.parentTitle ? 'Nowe podzadanie...' : 'Nowe zadanie...'} /> as HTMLInputElement;
  node.appendChild(input);
  if (hasDateFilter(filterState)) {
    input.disabled = true;
    input.placeholder = DATE_FILTER_MSG;
    input.title = DATE_FILTER_MSG;
    return node;
  }
  input.title = 'Wpisz tytuł i naciśnij Enter — zadanie trafi na koniec';
  const expand = () => {
    if (!node.isConnected) return;
    const heading = target.parentTitle ? 'Podzadanie dla: ' + target.parentTitle : '';
    const form = renderExpandedNewTask(target.parentId, target.position, callbacks, heading, () => form.replaceWith(node), input.value);
    form.style.marginLeft = node.style.marginLeft;
    input.value = '';
    node.replaceWith(form);
    (form.querySelector('.task-new-title') as HTMLInputElement | null)?.focus();
  };
  input.addEventListener('focus', expand);
  if (autoFocus) queueMicrotask(expand);
  return node;
}

/** Rozwinięty formularz nowego zadania: tytuł + 📅 ⏰ ⏳ ▷ 👤 👁 + tagi. Enter/„Dodaj” tworzy, Escape/„Anuluj” zwija. */
function renderExpandedNewTask(
  parentId: string | null,
  position: number | undefined,
  callbacks: NewTaskRowCallbacks,
  heading: string,
  onClose: () => void,
  initialTitle: string,
): HTMLElement {
  const wrapper = <div className="task-node task-node-new task-new-expanded" /> as HTMLElement;
  const form = <div className="task-new-form" role="form" aria-label="Nowe zadanie" /> as HTMLElement;
  wrapper.appendChild(form);

  if (heading) form.appendChild(<div className="task-new-heading">{heading}</div> as HTMLElement);

  const title = <input type="text" className="inline-new-input task-new-title"
    placeholder={parentId && heading ? 'Nowe podzadanie...' : 'Nowe zadanie tutaj...'} /> as HTMLInputElement;
  title.value = initialTitle;
  form.appendChild(title);

  const fields = <div className="task-new-fields" /> as HTMLElement;
  const addField = (icon: string, tip: string, control: HTMLElement) => {
    const label = <label className="task-new-field" title={tip}><span className="task-new-icon">{icon}</span></label> as HTMLElement;
    label.appendChild(control);
    fields.appendChild(label);
  };
  const deadline = <input type="date" className="inline-edit-input" /> as HTMLInputElement;
  const time = <input type="time" className="inline-edit-input" /> as HTMLInputElement;
  const minutes = <input type="text" className="inline-edit-input" placeholder="np. 1.5h" style={{ width: '80px' }} title={DURATION_HINT} /> as HTMLInputElement;
  minutes.addEventListener('change', () => {
    if (parseDuration(minutes.value) === undefined) {
      showToast('Nie rozumiem czasu „' + minutes.value.trim() + '” — ' + DURATION_HINT, 'error');
      minutes.value = '';
    }
  });
  const planned = <input type="date" className="inline-edit-input" /> as HTMLInputElement;
  const assignee = <input type="email" className="inline-edit-input" placeholder="email" style={{ width: '150px' }} /> as HTMLInputElement;
  const hidden = <input type="checkbox" /> as HTMLInputElement;
  // 🔁 od razu przy tworzeniu (spec/zadania.md → Zadania cykliczne) — ten sam picker co w meta row.
  const recurrence = renderRecurrencePicker({ value: null });
  addField('📅', 'Deadline', deadline);
  addField('⏰', 'Czas wykonania (wymaga deadline)', time);
  addField('⏳', 'Szacowana czasochłonność — ' + DURATION_HINT, minutes);
  addField('▷', 'Planowany start', planned);
  addField('👤', 'Przypisana osoba (email)', assignee);
  addField('👁', 'Ukryte — widoczne tylko dla Ciebie', hidden);
  addField('🔁', 'Cykliczność — po zamknięciu wystąpienia powstaje następne (wymaga deadline)', recurrence.el);
  form.appendChild(fields);

  // Tagi: przełączane badge'e, odziedziczone (rodzic / filtr) wstępnie zaznaczone.
  const selected = new Set(callbacks.getInheritedTagIds(parentId).map(t => t.tag_id));
  const allTags = callbacks.getAllTags();
  if (allTags.length > 0) {
    const tagsRow = <div className="task-new-tags" title="Tagi — kliknij, aby przełączyć" /> as HTMLElement;
    for (const tag of allTags) {
      const badge = renderTagBadge(tag);
      badge.classList.toggle('active', selected.has(tag.id));
      badge.addEventListener('click', (e: Event) => {
        e.stopPropagation();
        if (selected.has(tag.id)) selected.delete(tag.id); else selected.add(tag.id);
        badge.classList.toggle('active', selected.has(tag.id));
      });
      tagsRow.appendChild(badge);
    }
    form.appendChild(tagsRow);
  }

  const actions = <div className="task-new-actions" /> as HTMLElement;
  const addBtn = <button type="button" className="btn btn-primary btn-sm">Dodaj</button> as HTMLElement;
  const cancelBtn = <button type="button" className="btn btn-ghost btn-sm">Anuluj</button> as HTMLElement;
  actions.appendChild(addBtn);
  actions.appendChild(cancelBtn);
  form.appendChild(actions);

  const cleanup = () => document.removeEventListener('pointerdown', onOutside, true);
  const close = () => { cleanup(); onClose(); };
  // Klik poza formularz: z wpisanym tytułem = zapis (jak Enter), z pustym = zwinięcie.
  // Tylko Escape / „Anuluj” porzucają wpisane dane (spec: Tworzenie zadań).
  const onOutside = (e: Event) => {
    if (!wrapper.isConnected) { cleanup(); return; }
    if (wrapper.contains(e.target as Node)) return;
    if (title.value.trim()) submit(); else close();
  };
  document.addEventListener('pointerdown', onOutside, true);

  const baseInput = (): Omit<CreateTaskInput, 'title'> => ({
    parent_id: parentId || null,
    deadline: deadline.value || null,
    execution_time: deadline.value && time.value ? time.value : null,
    estimated_minutes: parseDuration(minutes.value) ?? null,
    planned_start_date: planned.value || null,
    assigned_email: assignee.value.trim() || null,
    hidden: hidden.checked,
    recurrence_rule: recurrence.getValue() ?? null,
    tag_ids: [...selected].map(tag_id => ({ tag_id })),
  });

  let submitting = false;
  const submit = async () => {
    const val = title.value.trim();
    if (!val) { title.focus(); return; }
    if (submitting) return;
    submitting = true;
    try {
      const created = await TasksAPI.create({ title: val, position, ...baseInput() });
      UndoManager.push({ type: 'create', taskId: created.id });
      close();
      callbacks.onCreated(parentId || null, created.position);   // następny formularz tuż pod utworzonym
      await callbacks.onRerender();
    } catch (err: unknown) {
      submitting = false;
      showToast('Błąd tworzenia zadania: ' + (err as Error).message, 'error');
    }
  };

  // Multi-paste: kilka linii → kilka zadań w tym miejscu, w kolejności linii (pozycje position, position+1, …).
  title.addEventListener('paste', (e: Event) => {
    const text = (e as ClipboardEvent).clipboardData?.getData('text') ?? '';
    const lines = text.split(/\r?\n/).map(l => l.replace(/^[\s\-*•\d+.)]+/, '').trim()).filter(Boolean);
    if (lines.length <= 1) return;
    e.preventDefault();
    (async () => {
      let created = 0;
      let lastPosition = -1;
      for (const [i, line] of lines.entries()) {
        try {
          const t = await TasksAPI.create({ title: line, position: position === undefined ? undefined : position + i, ...baseInput() });
          UndoManager.push({ type: 'create', taskId: t.id });
          created++;
          lastPosition = t.position;
        } catch (err: unknown) {
          showToast('Błąd tworzenia "' + line.substring(0, 30) + '": ' + (err as Error).message, 'error');
        }
      }
      if (created > 0) {
        showToast(`Utworzono ${created} zadań`, 'success');
        close();
        callbacks.onCreated(parentId || null, lastPosition);
        await callbacks.onRerender();
      }
    })();
  });

  addBtn.addEventListener('click', submit);
  cancelBtn.addEventListener('click', close);
  title.addEventListener('keydown', (e: Event) => {
    if ((e as KeyboardEvent).key === 'Enter') { e.preventDefault(); submit(); }
  });
  wrapper.addEventListener('keydown', (e: Event) => {
    if ((e as KeyboardEvent).key === 'Escape') { e.stopPropagation(); close(); }
  });

  setTimeout(() => title.focus(), 0);
  return wrapper;
}
